import {
  Check,
  ChevronDown,
  FolderOpen,
  Globe,
  Image,
  LogOut,
  MoreVertical,
  Palette,
  Plus,
  Ruler,
  Settings,
  Type,
  User,
  UserCog,
} from "lucide-react";
import type React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

export type DashboardView = "projects" | "published" | "variables-font" | "variables-colors" | "variables-sizes" | "variables-icons";

interface Craftspace {
  id: string;
  name: string;
  storage_provider?: "360crafter" | "custom-s3";
  icon_url?: string | null;
  order?: number | null;
  is_owner?: boolean;
  _source?: "local" | "cloud";
}

interface CraftspaceSidebarProps {
  craftspaces: Craftspace[];
  selectedCraftspaceId: string | null;
  onCraftspaceSelect: (craftspaceId: string) => void;
  onCreateCraftspace: () => void;
  activeView: DashboardView;
  onViewChange: (view: DashboardView) => void;
  onCraftspaceSettings?: (craftspaceId: string) => void;
  user?: { email?: string | null } | null;
  userName?: string | null;
  onUserPreferences?: () => void;
  onSignOut?: () => void;
  className?: string;
}

export const CraftspaceSidebar: React.FC<CraftspaceSidebarProps> = ({
  craftspaces,
  selectedCraftspaceId,
  onCraftspaceSelect,
  onCreateCraftspace,
  activeView,
  onViewChange,
  onCraftspaceSettings,
  user,
  userName,
  onUserPreferences,
  onSignOut,
  className = "",
}) => {
  const activeCraftspace = craftspaces.find((w) => w.id === selectedCraftspaceId) ?? craftspaces[0];

  const getCraftspaceInitials = (name: string): string => {
    if (!name) return "WS";
    if (name.length <= 2) return name.toUpperCase();
    if (name.includes(" ")) {
      return name.split(" ").map((w) => w.charAt(0)).join("").toUpperCase().slice(0, 2);
    }
    return name.toUpperCase().slice(0, 2);
  };

  const navItem = (
    view: DashboardView,
    icon: React.ReactNode,
    label: string,
  ) => {
    const active = activeView === view;
    return (
      <button
        type="button"
        onClick={() => onViewChange(view)}
        className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded text-sm transition-colors ${
          active
            ? "bg-white/10 text-white font-medium"
            : "text-gray-400 hover:text-white hover:bg-white/5"
        }`}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={`w-64 bg-[#111827] border-r border-gray-700/60 flex flex-col ${isElectron() ? "pt-10" : "pt-3"} pb-3 ${className}`}
      >
        {/* Craftspace Dropdown */}
        <div className="px-2 mb-4">
          <div className="flex items-stretch gap-1 bg-white/10 rounded-md">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex-1 flex items-center gap-2 px-2 py-2 rounded-md transition-colors group min-w-0"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-700 flex-shrink-0 text-xs font-semibold text-white overflow-hidden">
                    {activeCraftspace?.icon_url ? (
                      <img
                        src={activeCraftspace.icon_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getCraftspaceInitials(activeCraftspace?.name ?? "")
                    )}
                  </div>
                  <span className="flex-1 text-left text-sm font-medium text-white truncate">
                    {activeCraftspace?.name ?? "Select craftspace"}
                  </span>
                  <ChevronDown size={14} className="text-gray-400 group-hover:text-white flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
            <DropdownMenuContent
              side="bottom"
              align="start"
              sideOffset={4}
              className="w-52 bg-gray-800 border-gray-700 text-white"
            >
              {craftspaces.map((ws) => (
                <DropdownMenuItem
                  key={ws.id}
                  onClick={() => onCraftspaceSelect(ws.id)}
                  className="cursor-pointer flex items-center gap-2 hover:bg-gray-700 focus:bg-gray-700"
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded bg-gray-600 text-xs font-semibold flex-shrink-0 overflow-hidden">
                    {ws.icon_url ? (
                      <img src={ws.icon_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      getCraftspaceInitials(ws.name)
                    )}
                  </div>
                  <span className="flex-1 truncate text-sm">{ws.name}</span>
                  {ws.id === selectedCraftspaceId && (
                    <Check size={14} className="text-blue-400 flex-shrink-0" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-gray-700" />
              <DropdownMenuItem
                onClick={onCreateCraftspace}
                className="cursor-pointer flex items-center gap-2 hover:bg-gray-700 focus:bg-gray-700 text-gray-400 hover:text-white"
              >
                <Plus size={14} />
                <span className="text-sm">Create new craftspace</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
            </DropdownMenu>

            <div className="w-px h-full bg-gray-500" />

            {/* Three-dot context menu for selected craftspace */}
            {activeCraftspace && onCraftspaceSettings && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex-shrink-0 p-1.5 rounded-md text-gray-500 hover:text-white hover:bg-white/8 transition-colors focus:outline-none"
                    title="Craftspace options"
                  >
                    <MoreVertical size={15} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  align="end"
                  sideOffset={4}
                  className="bg-gray-800 border-gray-700 text-white"
                >
                  <DropdownMenuItem
                    onClick={() => onCraftspaceSettings(activeCraftspace.id)}
                    className="cursor-pointer flex items-center gap-2 hover:bg-gray-700 focus:bg-gray-700"
                  >
                    <Settings size={14} />
                    <span className="text-sm">Craftspace Settings</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <div className="px-2 flex flex-col gap-0.5">
          {navItem("projects", <FolderOpen size={15} />, "Projects")}
          {/* {navItem("published", <Globe size={15} />, "Published")} */}
        </div>

        {/* Variables Section */}
        <div className="px-2 mt-6">
          <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
            Craftspace Variables
          </p>
          <div className="flex flex-col gap-0.5">
            {navItem("variables-colors", <Palette size={15} />, "Colors")}
            {navItem("variables-icons", <Image size={15} />, "Icons")}
            {navItem("variables-sizes", <Ruler size={15} />, "Sizes")}
            {navItem("variables-font", <Type size={15} />, "Font")}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Profile */}
        {(user || onSignOut || onUserPreferences) && (
          <div className="px-2">
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white/8 transition-colors group focus:outline-none"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-600 hover:bg-gray-500 transition-colors flex-shrink-0">
                        <User size={15} className="text-white" />
                      </div>
                      {(userName || user?.email) && (
                        <span className="flex-1 text-left text-xs text-gray-400 truncate group-hover:text-gray-200">
                          {userName || user?.email}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>

              </Tooltip>
              <DropdownMenuContent side="top" align="start" className="bg-gray-800 border-gray-700 text-white">
                {onUserPreferences && (
                  <DropdownMenuItem
                    onClick={onUserPreferences}
                    className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  >
                    <UserCog size={14} className="mr-2" />
                    User Preferences
                  </DropdownMenuItem>
                )}
                {onSignOut && (
                  <>
                    {onUserPreferences && <DropdownMenuSeparator className="bg-gray-700" />}
                    <DropdownMenuItem
                      onClick={onSignOut}
                      className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                    >
                      <LogOut size={14} className="mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
